import React from "react";
import dayjs from "dayjs";
import { Checkbox, Form, Input, Select, Spin, Typography } from "../ui";
import { ThemedModal } from "../components/ThemedModal";
import { SecretValueInput } from "../forms/SecretInput";
import { DatePicker } from "../components/ControlledPickers";
import { spacing } from "../settings/settingsListDesign";
import { BankingFlickerCode } from "./BankingFlickerCode";
import { DialogActionContext, useDialogAction, useDialogActionScope } from "../ui/dialogActionScope";

/** A bank question is an independent action while the original bank request is still pending. */
function BankChallengeActions({ active, children }: { active: boolean; children: React.ReactNode }) {
  const request = useDialogAction();
  const question = useDialogActionScope(active);
  return <DialogActionContext.Provider value={active ? question.scope : request}>{children}</DialogActionContext.Provider>;
}

/** Verbindung, mit der ein Bankdialog arbeitet. accessMethod: 0 PIN/TAN, 1 RAH-10-Datei, 2 RDH-Datei. */
export type BankAccessConnection = {
  id: string; bankAccountId: string; accessMethod: 0 | 1 | 2; hasStoredPassport: boolean; hasStoredPin?: boolean;
};

export type BankingChallenge = {
  requestId: string; kind: "Choice" | "FileEntry" | "Tan" | "Approval" | "BankKey" | "PayeeCheck" | "TanMedium"; message: string;
  choices?: { value: string; label: string }[]; image?: string | null; flickerCode?: string | null; bankKeyFingerprint?: string | null;
  suggestedValue?: string | null;
};

/** Zugangsdaten eines Bankdialogs; der Aufrufer ergänzt sie um seinen Vorgang. */
export type BankAccessBody = { operationId: string; pin: string; initializeAccess: boolean; savePin: boolean | null; reselectSecurityMethod?: boolean; fetchFrom?: string };

/** Transport der Rückfragen; die Rückfrage ist serverseitig an Benutzer und Vorgang gebunden. */
export type BankChallengeTransport = {
  load: (bankAccountId: string, operationId: string) => Promise<BankingChallenge | null>;
  answer: (requestId: string, value: string) => Promise<void>;
};

export type BankAccessDialogProps<TResult> = {
  open: boolean;
  connection: BankAccessConnection | null;
  title: string;
  okText: string;
  /** Optionaler Inhalt über dem PIN-Feld, z. B. Zusammenfassung eines Auftrags. */
  summary?: React.ReactNode;
  /** Startet genau einen Bankvorgang. Ein Fehler wird im Dialog gezeigt, nicht wiederholt. */
  run: (body: BankAccessBody, signal: AbortSignal) => Promise<TResult>;
  /** Cancels a background test/fetch on the server before closing. Never supplied for payment orders. */
  cancelOperation?: (operationId: string) => Promise<{ completed: boolean; result?: TResult }>;
  transport: BankChallengeTransport;
  /**
   * Zähler der Echtzeit-Hinweise „Rückfrage wartet“ für dieses Konto. Jede Erhöhung lädt die Rückfrage einmal;
   * es gibt kein Polling.
   */
  challengeSignal: number;
  onClose: () => void;
  onBusy?: (busy: boolean) => void;
  onResult: (result: TResult) => void;
  /** Fehler des Antwortschritts; der Vorgang wird danach abgebrochen. */
  onAnswerError?: (message: string) => void;
  tr: (key: string) => string;
  /** Text eines geworfenen Fehlers; Standard ist seine Meldung. */
  errorText?: (error: unknown) => string;
  idPrefix?: string;
  /**
   * Abruf mit gespeicherten Zugangsdaten ohne PIN-Rückfrage starten; der Dialog zeigt den Wartestatus.
   * Nur für Abrufe — Tests und Aufträge zeigen den Dialog als Bestätigung des Vorgangs.
   */
  startWithStoredCredentials?: boolean;
  /** Nur Umsatzabrufe: null fordert die einmalige Wahl des Historienbeginns an. Ohne Prop kein Datumsfeld. */
  statementFetchFrom?: string | null;
  /** Explicit setup/test action; normal retrieval and payments always reuse a valid selection. */
  reselectSecurityMethod?: boolean;
  /** Erkennt den Zugangsfehler des Produkts (Ergebnis oder geworfener Fehler). Ohne ihn zählt kein Fehlversuch. */
  isCredentialsRejected?: (outcome: { result: TResult } | { error: unknown }) => boolean;
  /** Abgelehnte Anmeldungen je Vorgang, danach Abbruch. Standard 2. */
  maxCredentialAttempts?: number;
  /** Meldung nach dem Abbruch wegen zu vieler abgelehnter Anmeldungen. */
  onAttemptsExceeded?: (message: string) => void;
  /**
   * Der Vorgang läuft im Hintergrund des Servers (`useBankingOperationRun`).
   * Der Bankdialog zeigt währenddessen den Wartestatus und die Rückfragen.
   */
  background?: boolean;
};

/**
 * Gemeinsamer Bankdialog: PIN bzw. Datei-PIN, optional gespeicherte PIN, dann Rückfragen der Bank (TAN-Verfahren,
 * TAN mit Flicker-/Bildcode, App-Freigabe, Bankschlüsselprüfung, Empfängerprüfung). Ein Absenden startet genau einen
 * Bankvorgang; der Dialog wiederholt nichts selbst.
 */
export function BankAccessDialog<TResult>({ open, connection, title, okText, summary, run, transport, challengeSignal,
  onClose, onBusy, onResult, onAnswerError, tr, errorText, idPrefix = "bank-access", startWithStoredCredentials = false,
  isCredentialsRejected, maxCredentialAttempts = 2, onAttemptsExceeded, reselectSecurityMethod = false, cancelOperation, statementFetchFrom }: BankAccessDialogProps<TResult>) {
  const needsFetchStart = statementFetchFrom === null;
  const [fetchStartChoice, setFetchStartChoice] = React.useState<string | null | undefined>(undefined);
  const fetchStart = fetchStartChoice === undefined ? dayjs().startOf("month").format("YYYY-MM-DD") : fetchStartChoice;
  const [pin, setPin] = React.useState("");
  // Eine geänderte PIN darf den Zugang neu einrichten; eine unveränderte PIN erhält dessen Auswahl und Zustand.
  const initializeAccess = connection?.accessMethod === 0 && connection.hasStoredPassport && pin.length > 0;
  // Standardmäßig aus; ist bereits eine PIN gespeichert, steht das Häkchen an. Abwählen löscht sie.
  const [savePinChoice, setSavePin] = React.useState<boolean | null>(null);
  const hasStoredPin = !!connection?.hasStoredPin;
  const savePin = savePinChoice ?? hasStoredPin;
  // Nach einer Ablehnung hat das Backend die gespeicherte PIN verworfen; es zählt nur eine neue Eingabe.
  const [askCredentials, setAskCredentials] = React.useState(false);
  const useStoredPin = hasStoredPin && savePin && !initializeAccess && !askCredentials;
  // Ein anderer Fehler eines stillen Abrufs braucht ebenfalls eine sichtbare Fläche; die gespeicherte PIN bleibt nutzbar.
  const [revealed, setRevealed] = React.useState(false);
  const silent = startWithStoredCredentials && !needsFetchStart && hasStoredPin && (connection?.accessMethod === 0 || !!connection?.hasStoredPassport);
  const autoStarted = React.useRef(false);
  const rejectedAttempts = React.useRef(0);
  const [busy, setBusy] = React.useState(false);
  const [cancelling, setCancelling] = React.useState(false);
  const closing = React.useRef(false);
  const [answering, setAnswering] = React.useState(false);
  const answeringRequest = React.useRef<string | null>(null);
  const [challenge, setChallenge] = React.useState<BankingChallenge | null>(null);
  const currentChallenge = React.useRef<BankingChallenge | null>(null);
  const challengeLoad = React.useRef(0);
  const [answer, setAnswer] = React.useState("");
  const [error, setError] = React.useState<string | null>(null);
  const operation = React.useRef<AbortController | null>(null);
  const operationId = React.useRef("");
  const mounted = React.useRef(true);
  React.useEffect(() => { mounted.current = true; return () => { mounted.current = false; operation.current?.abort(); }; }, []);

  const bankAccountId = connection?.bankAccountId;
  React.useEffect(() => {
    if (!challengeSignal || !operation.current || !bankAccountId) return;
    const currentId = operationId.current;
    const loadId = ++challengeLoad.current;
    transport.load(bankAccountId, currentId).then((pending) => {
      if (operation.current && operationId.current === currentId && challengeLoad.current === loadId && mounted.current && pending
        && !closing.current && currentChallenge.current?.requestId !== pending.requestId) {
        currentChallenge.current = pending; setAnswer(pending.suggestedValue ?? ""); setChallenge(pending); setError(null);
      }
    }, reason => {
      if (mounted.current && operationId.current === currentId && challengeLoad.current === loadId)
        setError(errorText?.(reason) ?? ((reason as Error)?.message || tr("banking.failed")));
    });
  }, [challengeSignal, bankAccountId, transport, errorText, tr]);

  const describe = (e: unknown) => errorText?.(e) ?? ((e as Error)?.message || tr("banking.failed"));
  const clearAndClose = () => {
    setFetchStartChoice(undefined);
    setPin(""); setSavePin(null); setError(null); setChallenge(null); setAskCredentials(false); setRevealed(false);
    currentChallenge.current = null;
    autoStarted.current = false; rejectedAttempts.current = 0; onBusy?.(false); onClose();
  };
  // Eine abgelehnte PIN wird nie automatisch erneut gesendet; nach der Grenze bricht der Vorgang ab, bevor die Bank sperrt.
  const credentialsRejected = () => {
    rejectedAttempts.current += 1;
    if (rejectedAttempts.current >= maxCredentialAttempts) { onAttemptsExceeded?.(tr("banking.attemptsExceeded")); clearAndClose(); return; }
    setError(tr("banking.credentialsRequired")); setAskCredentials(true);
  };
  const cancel = async () => {
    if (closing.current) return;
    closing.current = true;
    if (operation.current && cancelOperation) {
      setCancelling(true);
      try {
        const outcome = await cancelOperation(operationId.current);
        if (!mounted.current) return;
        if (outcome.completed) onResult(outcome.result as TResult);
      }
      catch (reason) { if (!mounted.current) return; closing.current = false; setCancelling(false); setError(describe(reason)); setRevealed(true); return; }
    }
    operation.current?.abort(); clearAndClose(); closing.current = false;
    if (mounted.current) setCancelling(false);
  };
  const submit = async () => {
    if (!connection || operation.current) return;
    if (needsFetchStart && (!fetchStart || !dayjs(fetchStart).isValid() || dayjs(fetchStart).isAfter(dayjs(), "day"))) {
      setError(tr("banking.fetchFromInvalid")); return;
    }
    if (!pin && !useStoredPin) { setError(tr("banking.credentialsRequired")); return; }
    if (connection.accessMethod !== 0 && !connection.hasStoredPassport) { setError(tr("banking.fileRequired")); return; }
    const controller = new AbortController();
    operation.current = controller;
    operationId.current = crypto.randomUUID();
    setBusy(true); onBusy?.(true); setError(null);
    try {
      const result = await run({ operationId: operationId.current, pin, initializeAccess, savePin: savePin ? true : hasStoredPin ? false : null, reselectSecurityMethod,
        ...(needsFetchStart && fetchStart ? { fetchFrom: fetchStart } : {}) },
        controller.signal);
      if (!mounted.current || controller.signal.aborted || closing.current) return;
      if (isCredentialsRejected?.({ result })) { credentialsRejected(); return; }
      onResult(result); clearAndClose();
    } catch (e) {
      if (!mounted.current || controller.signal.aborted || closing.current) return;
      if (isCredentialsRejected?.({ error: e })) { credentialsRejected(); return; }
      // Ohne sichtbaren Dialog (Abruf mit gespeicherten Zugangsdaten) braucht der Fehler eine Fläche.
      setError(describe(e)); setRevealed(true);
    } finally {
      operation.current = null;
      operationId.current = "";
      if (mounted.current) { currentChallenge.current = null; setBusy(false); onBusy?.(false); setPin(""); setChallenge(null); setAnswer(""); }
    }
  };
  // Startet den Bankvorgang (externes System) einmal je Öffnen, wenn die gespeicherten Zugangsdaten reichen.
  const startSilently = React.useEffectEvent(() => { void submit(); });
  React.useEffect(() => {
    if (!open || !silent || autoStarted.current) return;
    autoStarted.current = true;
    startSilently();
  }, [open, silent]);
  const submitAnswer = async (decision?: "execute" | "cancel") => {
    if (!challenge || answeringRequest.current) return;
    // Empfängerprüfung: Ablehnen ist eine Antwort an die Bank, kein Abbruch des Vorgangs.
    const value = challenge.kind === "Approval" ? "confirmed" : challenge.kind === "PayeeCheck" ? decision ?? "execute" : answer;
    if (!value.trim()) return;
    answeringRequest.current = challenge.requestId;
    setAnswering(true);
    try {
      await transport.answer(challenge.requestId, value);
      if (currentChallenge.current?.requestId === challenge.requestId) {
        currentChallenge.current = null; setChallenge(null); setAnswer("");
      }
    } catch (e) { onAnswerError?.(describe(e)); await cancel(); }
    finally { answeringRequest.current = null; setAnswering(false); }
  };

  const formId = `${idPrefix}-form`;
  const challengeFormId = `${idPrefix}-challenge`;
  const challengeActive = !!challenge && !cancelling;
  const challengeTitle = tr(challenge?.kind === "Approval" ? "banking.appApproval" : challenge?.kind === "BankKey" ? "banking.verifyBankKey"
    : challenge?.kind === "FileEntry" ? "banking.securityFileEntry" : challenge?.kind === "PayeeCheck" ? "banking.payeeCheck"
    : challenge?.kind === "Choice" ? "banking.selectTanMethod" : challenge?.kind === "TanMedium" ? "banking.selectTanMedium" : "banking.enterTan");
  return <ThemedModal title={challengeActive ? challengeTitle : title}
      modalRender={node => <BankChallengeActions active={challengeActive}>{node}</BankChallengeActions>}
      open={open && (challengeActive || busy || cancelling || !silent || askCredentials || revealed)}
      onCancel={challengeActive && challenge.kind === "PayeeCheck" ? () => submitAnswer("cancel") : cancel}
      formId={challengeActive ? challengeFormId : formId}
      cancelButtonProps={{ disabled: cancelling }} closable={!cancelling} keyboard={!cancelling}
      pendingPresentation="visible"
      okButtonProps={{ disabled: challengeActive ? answering || ((challenge.kind === "Choice" || challenge.kind === "TanMedium" || challenge.kind === "FileEntry" || challenge.kind === "Tan" || challenge.kind === "BankKey") && !answer.trim()) : busy }}
      okText={challengeActive ? tr(challenge.kind === "Approval" ? "banking.approvalCompleted" : challenge.kind === "PayeeCheck" ? "banking.executeOrder" : "banking.continue") : okText}
      cancelText={challengeActive && challenge.kind === "PayeeCheck" ? tr("banking.cancelOrder") : undefined}
      autoFocusFirstField={challengeActive ? challenge.kind !== "PayeeCheck" : !busy} destroyOnHidden>
      {!challengeActive ? <Form key="credentials" id={formId} layout="vertical" autoComplete="off" onFinish={() => submit()}>
        {(busy || cancelling) && <Spin description={tr(cancelling ? "banking.cancelling" : "banking.processing")} />}
        {summary}
        {!busy && <>
        {needsFetchStart && <Form.Item label={tr("banking.fetchFrom")} htmlFor={`${idPrefix}-fetch-from`} required>
          <DatePicker id={`${idPrefix}-fetch-from`} value={fetchStart ? dayjs(fetchStart) : null}
            onChange={value => setFetchStartChoice(value?.format("YYYY-MM-DD") ?? null)} disabledDate={value => value.isAfter(dayjs(), "day")} />
        </Form.Item>}
        {!useStoredPin && <>
        <Form.Item label={tr(connection?.accessMethod === 0 ? "banking.pin" : "banking.filePassword")} htmlFor={`${idPrefix}-pin`} required>
          <SecretValueInput id={`${idPrefix}-pin`} value={pin} onChange={(e) => setPin(e.target.value)} disabled={busy}
            showText={tr("banking.pin")} hideText={tr("banking.pin")} />
        </Form.Item>
        <Form.Item>
          <Checkbox checked={savePin} disabled={busy} onChange={(e) => setSavePin(e.target.checked)}>{tr(connection?.accessMethod === 0 ? "banking.savePin" : "banking.saveFilePassword")}</Checkbox>
        </Form.Item>
        </>}
        </>}
        {error && <Typography.Paragraph type="danger">{error}</Typography.Paragraph>}
      </Form> : <Form key="challenge" id={challengeFormId} layout="vertical" disabled={answering} onFinish={() => submitAnswer()} autoComplete="off">
        {error && <Typography.Paragraph type="danger">{error}</Typography.Paragraph>}
        {answering && <Spin description={tr("banking.processing")} />}
        {challenge?.kind === "Choice" ? <Typography.Paragraph>{tr("banking.tanMethodInstruction")}</Typography.Paragraph>
          : challenge?.kind === "TanMedium" ? <Typography.Paragraph>{tr("banking.tanMediumInstruction")}</Typography.Paragraph>
          : challenge?.kind === "FileEntry" ? <Typography.Paragraph>{tr("banking.fileEntryInstruction")}</Typography.Paragraph>
          : challenge?.kind === "Approval" ? <>
            <Typography.Paragraph>{tr("banking.appApprovalInstruction")}</Typography.Paragraph>
            {challenge.message && <Typography.Paragraph style={{ whiteSpace: "pre-line" }}>{challenge.message}</Typography.Paragraph>}
          </>
          : challenge?.kind === "BankKey" ? null
          : <Typography.Paragraph style={{ whiteSpace: "pre-line" }}>{challenge?.message}</Typography.Paragraph>}
        {challenge?.kind === "PayeeCheck" && <Typography.Paragraph type="warning">{tr("banking.payeeCheckInstruction")}</Typography.Paragraph>}
        {challenge?.flickerCode && <BankingFlickerCode key={challenge.requestId} code={challenge.flickerCode} tr={tr} />}
        {challenge?.image && /^data:image\/(png|jpeg|gif);base64,/.test(challenge.image) && <img src={challenge.image} alt={tr("banking.tanImage")}
          style={{ maxWidth: "100%", marginBottom: spacing.md }} />}
        {challenge?.kind === "BankKey" && <>
          <Typography.Paragraph>{tr("banking.verifyBankKeyInstruction")}</Typography.Paragraph>
          <Typography.Paragraph code>{challenge.bankKeyFingerprint}</Typography.Paragraph>
          <Form.Item label={tr("banking.bankDocumentFingerprint")} htmlFor={`${idPrefix}-fingerprint`} required>
            <Input id={`${idPrefix}-fingerprint`} value={answer} onChange={(e) => setAnswer(e.target.value)} maxLength={256} autoComplete="off" />
          </Form.Item>
        </>}
        {(challenge?.kind === "Choice" || challenge?.kind === "FileEntry") && <Form.Item label={tr(challenge.kind === "FileEntry" ? "banking.securityFileEntry" : "banking.tanMethod")}
          htmlFor={`${idPrefix}-choice`} required>
          <Select id={`${idPrefix}-choice`} value={answer || undefined} options={challenge.choices} onChange={setAnswer} />
        </Form.Item>}
        {challenge?.kind === "TanMedium" && <Form.Item label={tr("banking.tanMedium")} htmlFor={`${idPrefix}-medium`} required>
          <Select id={`${idPrefix}-medium`} value={answer || undefined} options={challenge.choices} onChange={setAnswer} disabled={answering} />
        </Form.Item>}
        {challenge?.kind === "Tan" && <Form.Item label={tr("banking.enterTan")} htmlFor={`${idPrefix}-tan`} required>
          <Input id={`${idPrefix}-tan`} value={answer} onChange={(e) => setAnswer(e.target.value)} maxLength={256} autoComplete="off" />
        </Form.Item>}
      </Form>}
    </ThemedModal>;
}
